import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ContextService } from './context.service';

/**
 * Service for handling permission checks and authorization
 * Now uses ContextService for permissions and roles
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private contextService = inject(ContextService);

  /**
   * Checks if user has required roles
   */
  hasRoles(requiredRoles: string[]): Observable<boolean> {
    return this.contextService.activePortal$.pipe(
      map(activePortal => {
        if (!activePortal) return false;
        const roles = this.contextService.getRoles();
        return requiredRoles.every(role => roles.includes(role));
      })
    );
  }

  /**
   * Checks if user has any of the required roles
   */
  hasAnyRole(requiredRoles: string[]): Observable<boolean> {
    return this.contextService.activePortal$.pipe(
      map(activePortal => {
        if (!activePortal) return false;
        const roles = this.contextService.getRoles();
        return requiredRoles.some(role => roles.includes(role));
      })
    );
  }

  /**
   * Checks if user has a specific permission string
   * Permissions are stored in ContextService
   */
  hasPermission(permission: string): Observable<boolean> {
    return this.contextService.permissions$.pipe(
      map(permissions => {
        if (!permissions) return false;
        return permissions.permissions.includes(permission);
      })
    );
  }

  /**
   * Checks if user has any of the required permissions
   */
  hasAnyPermission(requiredPermissions: string[]): Observable<boolean> {
    return this.contextService.permissions$.pipe(
      map(permissions => {
        if (!permissions) return false;
        return requiredPermissions.some(perm => permissions.permissions.includes(perm));
      })
    );
  }

  /**
   * Checks if user has all of the required permissions
   */
  hasAllPermissions(requiredPermissions: string[]): Observable<boolean> {
    return this.contextService.permissions$.pipe(
      map(permissions => {
        if (!permissions) return false;
        return requiredPermissions.every(perm => permissions.permissions.includes(perm));
      })
    );
  }

  /**
   * Checks if user has client permission for a specific MCode
   * @deprecated Use ContextService.getClients() instead
   */
  hasClientPermission(mCode: string): Observable<boolean> {
    return this.contextService.permissions$.pipe(
      map(permissions => {
        if (!permissions) return false;
        return permissions.clients.some(client => client.mCode === mCode);
      })
    );
  }

  /**
   * Checks if user has client permission for a specific MCode and CCode
   * @deprecated Use ContextService.getClients() instead
   */
  hasClientSubClientPermission(mCode: string, cCode: string): Observable<boolean> {
    return this.contextService.permissions$.pipe(
      map(permissions => {
        if (!permissions) return false;
        const client = permissions.clients.find(c => c.mCode === mCode);
        if (!client) return false;
        return client.subClients.some(subClient => subClient.cCode === cCode);
      })
    );
  }
}
